import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AdminWithdrawals = () => {
  const navigate = useNavigate();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [stats, setStats] = useState({
    pending: 0,
    pendingAmount: 0,
    approved: 0,
    approvedAmount: 0,
    rejected: 0,
    rejectedAmount: 0
  });

  useEffect(() => {
    loadWithdrawals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadWithdrawals = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const url = filter === 'all' 
        ? `${API_URL}/admin/withdrawals`
        : `${API_URL}/admin/withdrawals?status=${filter}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setWithdrawals(response.data.withdrawals || []);
      
      // Calculate stats
      const pending = response.data.withdrawals.filter(w => w.status === 'pending');
      const approved = response.data.withdrawals.filter(w => w.status === 'approved');
      const rejected = response.data.withdrawals.filter(w => w.status === 'rejected');

      setStats({
        pending: pending.length,
        pendingAmount: pending.reduce((sum, w) => sum + parseFloat(w.amount), 0),
        approved: approved.length,
        approvedAmount: approved.reduce((sum, w) => sum + parseFloat(w.amount), 0),
        rejected: rejected.length,
        rejectedAmount: rejected.reduce((sum, w) => sum + parseFloat(w.amount), 0)
      });

      setError('');
    } catch (err) {
      console.error('Erreur chargement retraits:', err);
      setError(err.response?.data?.error || 'Erreur de chargement');
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId) => {
    if (!window.confirm('Confirmer l\'approbation de ce retrait ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/admin/withdrawals/${withdrawalId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Retrait approuvé avec succès');
      loadWithdrawals();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'approbation');
    }
  };

  const handleReject = async (withdrawalId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    if (reason === null) return; // User cancelled

    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/admin/withdrawals/${withdrawalId}/reject`,
        { admin_note: reason || 'Rejeté par l\'administrateur' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Retrait rejeté');
      loadWithdrawals();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du rejet');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-withdrawals">
          <div className="loading">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-withdrawals">
        <div className="page-header">
          <h1>Demandes de Retrait</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Statistiques */}
        <div className="stats-grid">
          <div className="stat-card stat-pending">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <div className="stat-label">En Attente</div>
              <div className="stat-value">{stats.pending}</div>
              <div className="stat-amount">{stats.pendingAmount.toLocaleString()} FCFA</div>
            </div>
          </div>

          <div className="stat-card stat-approved">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-label">Approuvés</div>
              <div className="stat-value">{stats.approved}</div>
              <div className="stat-amount">{stats.approvedAmount.toLocaleString()} FCFA</div>
            </div>
          </div>

          <div className="stat-card stat-rejected">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <div className="stat-label">Rejetés</div>
              <div className="stat-value">{stats.rejected}</div>
              <div className="stat-amount">{stats.rejectedAmount.toLocaleString()} FCFA</div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="filters-bar">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Tous
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            En attente ({stats.pending})
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approuvés ({stats.approved})
          </button>
          <button
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejetés ({stats.rejected})
          </button>
        </div>

        {/* Liste des retraits */}
        <div className="withdrawals-section">
          {withdrawals.length === 0 ? (
            <div className="empty-state">
              <p>Aucune demande de retrait</p>
            </div>
          ) : (
            <div className="withdrawals-grid">
              {withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className={`withdrawal-card status-${withdrawal.status}`}>
                  <div className="withdrawal-header">
                    <div className="reseller-info">
                      <h3>{withdrawal.reseller_name}</h3>
                      <p className="reseller-email">{withdrawal.reseller_email}</p>
                    </div>
                    <span className={`status-badge status-${withdrawal.status}`}>
                      {withdrawal.status === 'pending' && '⏳ En attente'}
                      {withdrawal.status === 'processing' && '🔄 Payout envoyé'}
                      {withdrawal.status === 'approved' && '✅ Approuvé'}
                      {withdrawal.status === 'rejected' && '❌ Rejeté'}
                      {withdrawal.status === 'failed' && '💥 Échoué'}
                    </span>
                  </div>

                  <div className="withdrawal-body">
                    <div className="info-row">
                      <span className="label">Montant:</span>
                      <span className="amount">{parseFloat(withdrawal.amount).toLocaleString()} FCFA</span>
                    </div>

                    <div className="info-row">
                      <span className="label">Méthode:</span>
                      <span className="value">{withdrawal.payment_method}</span>
                    </div>

                    <div className="info-row">
                      <span className="label">Numéro:</span>
                      <span className="value payment-number">{withdrawal.payment_details}</span>
                    </div>

                    <div className="info-row">
                      <span className="label">Date demande:</span>
                      <span className="value">{new Date(withdrawal.requested_at).toLocaleString('fr-FR')}</span>
                    </div>

                    {withdrawal.processed_at && (
                      <div className="info-row">
                        <span className="label">Date traitement:</span>
                        <span className="value">{new Date(withdrawal.processed_at).toLocaleString('fr-FR')}</span>
                      </div>
                    )}

                    {withdrawal.admin_note && (
                      <div className="admin-note">
                        <strong>Note admin:</strong>
                        <p>{withdrawal.admin_note}</p>
                      </div>
                    )}
                  </div>

                  {withdrawal.status === 'pending' && (
                    <div className="withdrawal-actions">
                      <button
                        onClick={() => handleApprove(withdrawal.id)}
                        className="btn-approve"
                      >
                        ✅ Approuver & Envoyer
                      </button>
                      <button
                        onClick={() => handleReject(withdrawal.id)}
                        className="btn-reject"
                      >
                        ❌ Rejeter
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminWithdrawals;
